<template>
  <v-card outlined tile :style="`border-color: ${color}`">
    <div :style="`background-color: ${color}; height: 18px`"></div>
    <v-card-title class="subtitle-1">
      Assign Tenant Device
      <v-spacer />
      <v-btn class="mr-2" color="primary" @click="showQRDialog = true">
        <v-icon left>fas fa-qrcode</v-icon>
        Scan Device
      </v-btn>
      <div style="min-width: 240px">
        <SearchField v-model="search" />
      </div>
    </v-card-title>

    <QRCodeDialog
      v-model="showQRDialog"
      title="Assign Tenant Device"
      description="Scan this QR code with your mobile phone or tablet to begin assigning the devices."
      :url-generator="generateInventoryUrl"
    />
    <v-data-table
      dense
      :headers="headers"
      :items="rows"
      :loading="loading"
      :search="search"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
      no-data-text="No units awaiting device assignment"
    >
      <template #item.tenantName="{ item }">
        {{ item.responderFirstName }} {{ item.responderLastName }}
      </template>
      <template #item.phone="{ item }">
        {{ item.phone || "—" }}
      </template>
      <template #item.moveInAt="{ item }">
        {{ item.moveInAt ? formatDate(item.moveInAt) : "—" }}
      </template>
      <template #item.daysWithoutDevice="{ item }">
        <AwaitingDeviceDaysPill :days="item.daysWithoutDevice" />
      </template>
      <template #item.action="{ item }">
        <div
          class="d-flex align-center justify-space-between"
          style="gap: 8px; width: 100%"
        >
          <v-btn small color="success" @click="assignDevice(item)">
            Assign
          </v-btn>
          <ButtonEndService
            v-if="isEndServiceEligibleByMoveIn(item.moveInAt)"
            :unit="endServiceUnit(item)"
            :ended-service="item.billingAccount?.status === 'ended_service'"
            :sync-obj="{ noSyncCheck: true }"
            @submit="
              ({ value, cause }) => onEndServiceSubmit(item, value, cause)
            "
          />
        </div>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { formatDate } from "@tod-ui/helpers/datetime";
import { formatPhoneNumber } from "@tod-ui/helpers/phone";
import { displayUnitType } from "@tod-ui/helpers/unitType";
import SearchField from "@atoms/SearchField.vue";
import AwaitingDeviceDaysPill from "../smart_units/AwaitingDeviceDaysPill.vue";
import QRCodeDialog from "../mobile/QRCodeDialog.vue";
import ButtonEndService from "../common/ButtonEndService.vue";
import useFacility from "../authentication/useFacility";
import useEndService from "../common/useEndService";
import useAlerts from "@tod-ui/composables/useAlerts";
import {
  MISSING_DEVICE_UNITS,
  MISSING_DEVICE_SMART_UNITS_COUNT,
} from "../smart_units/graphql";
import {
  isAwaitingDevice,
  isEndServiceEligibleByMoveIn,
} from "../smart_units/helper";

export default {
  name: "AwaitingDeviceInfoTable",
  components: {
    SearchField,
    AwaitingDeviceDaysPill,
    QRCodeDialog,
    ButtonEndService,
  },
  props: {
    color: {
      type: String,
      default: "#7b1818",
    },
    isRevmax: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { currentFacilityId } = useFacility();
    const { removeDeviceAndUnassignUnit, endServiceForAccount } =
      useEndService();
    const { addAlert } = useAlerts();
    return {
      currentFacilityId,
      removeDeviceAndUnassignUnit,
      endServiceForAccount,
      addAlert,
    };
  },
  data: () => ({
    search: "",
    sortBy: ["moveInAt"],
    sortDesc: [true],
    units: [],
    loading: true,
    showQRDialog: false,
  }),
  apollo: {
    units: {
      query: MISSING_DEVICE_UNITS,
      variables() {
        return { id: this.currentFacilityId };
      },
      fetchPolicy: "no-cache",
      update({ smartUnits }) {
        this.loading = false;
        return smartUnits || [];
      },
    },
  },
  computed: {
    headers() {
      const base = [
        { text: "Smart Unit #", value: "name" },
        { text: "Unit Type", value: "unitTypeName" },
        { text: "Unit Size", value: "unitTypeSize" },
        { text: "Tenant Name", value: "tenantName", sortable: false },
        { text: "Phone", value: "phone", sortable: false },
        { text: "Move-In Date", value: "moveInAt" },
      ];
      if (this.isRevmax) {
        base.push({ text: "Days Without Device", value: "daysWithoutDevice" });
      }
      base.push({
        text: "Action",
        value: "action",
        align: "center",
        sortable: false,
        width: "1%",
      });
      return base;
    },
    rows() {
      const units = this.isRevmax
        ? this.units.filter(isAwaitingDevice)
        : this.units;
      return units.map((u) => {
        const responder = u.responders?.[0];
        const contact = responder?.contactMethod;
        const isPhone = ["phone", "mobile"].includes(contact?.type);
        const billingUser = u.billingAccounts?.[0]?.users?.[0];
        return {
          id: u.id,
          name: u.name,
          unitTypeName: displayUnitType(u?.unitType, "type"),
          unitTypeSize: displayUnitType(u?.unitType, "size"),
          renterId: responder?.user?.id || billingUser?.id || null,
          responderFirstName:
            responder?.user?.firstName || billingUser?.firstName || "",
          responderLastName:
            responder?.user?.lastName || billingUser?.lastName || "",
          phone: isPhone ? formatPhoneNumber(contact?.value) : null,
          moveInAt:
            u.enrollmentInfo?.moveInAt || u.billingAccounts?.[0]?.insertedAt,
          daysWithoutDevice: u.enrollmentInfo?.daysWithoutDevice ?? null,
          billingAccount: u.billingAccounts?.[0] || null,
        };
      });
    },
  },
  methods: {
    formatDate,
    isEndServiceEligibleByMoveIn,
    assignDevice(item) {
      this.$router.push({
        name: "ManageSmartUnitDevices",
        params: { zone_id: item.id },
      });
    },
    generateInventoryUrl(qrToken) {
      const baseUrl = window.location.origin;
      return `${baseUrl}/mobile-inventory?qrToken=${encodeURIComponent(
        qrToken
      )}&facility=${this.currentFacilityId}`;
    },
    endServiceUnit(item) {
      // This tile only lists units with no device assigned, so there is
      // never a device to leave behind or send back to inventory here.
      return { id: item.id, name: item.name, devices: [] };
    },
    async onEndServiceSubmit(item, value, cause) {
      if (!item.billingAccount) {
        this.addAlert({
          type: "error",
          message: `No billing account found for Smart Unit ${item.name} — unable to end service.`,
        });
        return;
      }
      const refetchQueries = [
        {
          query: MISSING_DEVICE_UNITS,
          variables: { id: this.currentFacilityId },
        },
        {
          query: MISSING_DEVICE_SMART_UNITS_COUNT,
          variables: { id: this.currentFacilityId },
        },
      ];
      if (value) {
        await this.removeDeviceAndUnassignUnit(
          this.$apollo,
          this.endServiceUnit(item),
          item.billingAccount.id,
          this.currentFacilityId,
          refetchQueries
        );
      } else {
        await this.endServiceForAccount(
          this.$apollo,
          item.billingAccount,
          cause,
          refetchQueries
        );
      }
    },
  },
};
</script>
